import React, { Fragment, useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Spinner, Alert } from 'reactstrap';
import { Breadcrumbs, H4 } from '../../../../AbstractElements';
import alertApi from '../../../../api/alert';
import AlertCard from './AlertCard';
import AlertModal from './AlertModal';
import AlertFilters from './AlertFilters';

const AlertContain = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal
  const [modal, setModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    type: 'success',
    key: '',
    message: '',
    description: '',
    sortOrder: 0,
    isActive: true
  });

  // Load alerts
  useEffect(() => {
    loadAlerts();
  }, [typeFilter, statusFilter]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {};
      if (typeFilter !== 'all') params.type = typeFilter;
      if (statusFilter !== 'all') params.isActive = statusFilter === 'active';
      
      const response = await alertApi.getAllAlerts(params);
      setAlerts(response.data?.alerts || response.alerts || response.data || response);
    } catch (err) {
      console.error('❌ Error loading alerts:', err);
      setError('Không thể tải danh sách alerts');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      setEditingAlert(null);
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'success',
      key: '',
      message: '',
      description: '',
      sortOrder: 0,
      isActive: true
    });
  };

  const handleEdit = (alert) => {
    setEditingAlert(alert);
    setFormData({
      type: alert.type,
      key: alert.key,
      message: alert.message,
      description: alert.description || '',
      sortOrder: alert.sortOrder || 0,
      isActive: alert.isActive
    });
    setModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      
      if (editingAlert) {
        await alertApi.updateAlert(editingAlert.id, formData);
        setSuccess('Cập nhật alert thành công!');
      } else {
        await alertApi.createAlert(formData);
        setSuccess('Tạo alert thành công!');
      }
      
      toggleModal();
      loadAlerts();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error saving alert:', err);
      setError('Không thể lưu alert');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa alert này?')) return;
    
    try {
      await alertApi.deleteAlert(id);
      setSuccess('Xóa alert thành công!');
      loadAlerts();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error deleting alert:', err);
      setError('Không thể xóa alert');
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await alertApi.toggleAlertStatus(id, !currentStatus);
      setSuccess('Cập nhật trạng thái thành công!');
      loadAlerts();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error toggling status:', err);
      setError('Không thể cập nhật trạng thái');
    }
  };

  if (loading) {
    return (
      <Fragment>
        <Breadcrumbs parent='Apps' title='Alert Management' mainTitle='Alert Management' />
        <Container fluid={true}>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 mb-0">Đang tải alerts...</p>
          </div>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumbs parent='Apps' title='Alert Management' mainTitle='Quản lý Alerts' />
      <Container fluid={true}>
        {/* Success/Error Messages */}
        {success && <Alert color="success">{success}</Alert>}
        {error && <Alert color="danger">{error}</Alert>}

        {/* Header Card */}
        <Card className="shadow-sm border-0 mb-4">
          <CardBody className="p-4">
            <Row className="align-items-center">
              <Col md="6">
                <H4 className="text-dark mb-0">
                  <i className="fa fa-bell me-2 text-primary"></i>
                  Quản lý Alert Messages
                </H4>
              </Col>
              <Col md="6" className="text-end">
                <Button color="primary" onClick={toggleModal}>
                  <i className="fa fa-plus me-2"></i>
                  Tạo Alert Mới
                </Button>
              </Col>
            </Row>

            {/* Filters */}
            <AlertFilters 
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
            />
          </CardBody>
        </Card>

        {/* Alerts List */}
        <Row>
          {alerts.length === 0 ? (
            <Col>
              <Card className="text-center py-5">
                <CardBody>
                  <i className="fa fa-bell-slash text-muted" style={{fontSize: '3rem'}}></i>
                  <p className="text-muted mt-3">Chưa có alert nào</p>
                </CardBody>
              </Card>
            </Col>
          ) : (
            alerts.map((alert) => (
              <Col lg="6" key={alert.id}>
                <AlertCard 
                  alert={alert}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                />
              </Col>
            ))
          )}
        </Row>

        {/* Create/Edit Modal */}
        <AlertModal 
          isOpen={modal}
          toggle={toggleModal}
          editingAlert={editingAlert}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
        />
      </Container>
    </Fragment>
  );
};

export default AlertContain;
